import './BuyNowButton.css';

import { EmailOutlined } from '@material-ui/icons';
import React, { useState } from 'react';

import { AdditionalInsured, QuoteInfo } from '../../../../generated/graphql';
import getEnv from '../../../../utils/getEnv';
import { useFlags } from '../../../../utils/useFlags';
import PaymentMethod from '../PaymentMethod';
import QuoteLinkPopup from '../QuoteLinkPopup';
import Popup from './Popup';

const { REACT_APP_BUY_NOW_TRACKING_URL } = getEnv();
interface BuyNowButtonProps {
  munichQuoteId?: string;
  amtrustQuoteId?: number;
  applicationId: string;
  showSendQuoteButton: boolean;
  country: string;
  quoteInfo?: QuoteInfo;
  disabled: boolean;
  setQuoteId: React.Dispatch<React.SetStateAction<string | undefined>>;
  additionalInsuredInfoList?: AdditionalInsured[];
  validateAdditionalInsured: () => boolean;
}

const FOXQUILT_TERMS_OF_USE = 'https://www.foxquilt.com/terms-of-use.html';
const FOXQUILT_PRIVACY_NOTICE = 'https://www.foxquilt.com/privacy-policy.html';

interface BuyNowButtonTrackingData {
  applicationId: string;
  munichQuoteId: string;
}
const buyNowButtonTracking = (
  buyNowButtonTrackingData: BuyNowButtonTrackingData
) => {
  try {
    fetch(REACT_APP_BUY_NOW_TRACKING_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buyNowButtonTrackingData)
    });
  } catch (e) {
    // don't show error of this api call to client
  }
};

const BuyNowButton: React.FC<BuyNowButtonProps> = (props) => {
  const flags = useFlags();
  const {
    munichQuoteId,
    applicationId,
    country,
    quoteInfo,
    disabled,
    setQuoteId,
    additionalInsuredInfoList,
    validateAdditionalInsured
  } = props;
  const [showPopup, setShowPopup] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const isEndorsement = quoteInfo?.transactionType === 'Endorsement';
  const isCancellation = quoteInfo?.transactionType === 'Cancellation';
  const { amTrustFullFlow } = flags;
  const closePopup = () => {
    setShowPopup(false);
  };

  if (!amTrustFullFlow && !munichQuoteId) {
    throw new Error('do not support invalid munichQuote yet');
  }

  const buttonName = isEndorsement ? 'CHANGE POLICY' : 'BUY NOW';

  return (
    <div className="confirm-pay-container lg:max-w-xs text-center sm:mt-0">
      <p>
        <button
          disabled={disabled}
          className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 mb-2 text-mobileFont md:text-xl text-white confirm-buy"
          onClick={() => {
            // Validate the Additional Insured information
            const validationResult = validateAdditionalInsured();
            if (validationResult) {
              setShowPopup(true);
              if (munichQuoteId) {
                buyNowButtonTracking({ applicationId, munichQuoteId });
              }
            } else {
              const additionalInsuredEle = document.getElementById(
                'additional-insured-container'
              );
              additionalInsuredEle
                ? additionalInsuredEle.scrollIntoView({ behavior: 'smooth' })
                : null;
            }
          }}
        >
          {buttonName}
        </button>
      </p>

      <p className="text-center text-xs mt-2 mx-auto">
        By clicking {buttonName}, you agree to Foxquilt&apos;s{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_TERMS_OF_USE}
          target="_blank"
          rel="noreferrer"
        >
          Terms of Use
        </a>{' '}
        and{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_PRIVACY_NOTICE}
          target="_blank"
          rel="noreferrer"
        >
          Privacy Notice
        </a>
      </p>
      <p className="text-center mt-2 text-xs mb-2">
        Your coverage will not be effective until payment is confirmed
      </p>
      <PaymentMethod />
      <div className="border-gray-300 border-t-2 border-b-2 py-4">
        <button
          className="w-full border-2 border-faded rounded-md bg-white hover:bg-background text-quartary font-semibold py-1 px-3 md:px-14"
          onClick={() => setDisplayModal(true)}
        >
          <EmailOutlined className="mb-1 mr-2" />
          Send Quote
        </button>
      </div>

      {showPopup && munichQuoteId ? (
        <Popup
          closeAction={closePopup}
          quoteId={munichQuoteId}
          isEndorsement={isEndorsement}
          isCancellation={isCancellation}
          quoteInfo={quoteInfo}
          applicationId={applicationId}
          country={country}
          additionalInsuredInfoList={additionalInsuredInfoList}
          setQuoteId={setQuoteId}
        />
      ) : null}

      {quoteInfo ? (
        <QuoteLinkPopup
          displayModal={displayModal}
          handleClose={() => setDisplayModal(false)}
          applicationId={applicationId}
          quoteInfo={quoteInfo}
          additionalInsuredInfoList={additionalInsuredInfoList}
          setQuoteId={setQuoteId}
        />
      ) : null}
    </div>
  );
};

export default BuyNowButton;
